import React from 'react';
import {
  IonCard,
  IonCardHeader,
  IonToolbar,
  IonButtons,
  IonButton,
  IonIcon,
  IonTitle,
  IonGrid,
  IonRow,
  IonCol,
  IonInput,
} from '@ionic/react';
import { arrowBack, mail } from 'ionicons/icons';
import styles from '../../pages/login/LoginReg.module.scss';
import { useTranslation } from 'react-i18next';

// Phone formatting function for display
const formatPhoneDisplay = (phone: string): string => {
  if (!phone) return '';

  // Remove all non-digits except +
  const cleaned = phone.replace(/[^\d+]/g, '');

  // Always format as US number with +1
  const digits = cleaned.startsWith('+1') ? cleaned.substring(2) :
    cleaned.startsWith('1') ? cleaned.substring(1) :
      cleaned.startsWith('+') ? cleaned.substring(1) : cleaned;

  if (digits.length === 0) return '+1 ';
  if (digits.length <= 3) return `+1 (${digits}`;
  if (digits.length <= 6) return `+1 (${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

// Phone validation function
const isValidPhoneFormat = (phone: string): boolean => {
  const cleaned = phone.replace(/[^\d]/g, '');
  return cleaned.length === 11 && cleaned.startsWith('1');
};

interface RegisterCardProps {
  formData: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    password: string;
    confirmPassword: string;
  };
  uiState: {
    showPassword: boolean;
    loggingIn: boolean;
    registering: boolean;
  };
  authLoading: boolean;
  isDisabled: boolean;
  onGoBack: () => void;
  onRegister: () => void;
  onContinue?: () => void;
  onInputChange: (field: string, value: string) => void;
}

export const RegisterCard: React.FC<RegisterCardProps> = ({
  formData,
  isDisabled,
  onGoBack,
  onContinue,
  onInputChange
}) => {
  const { t } = useTranslation();

  // Custom phone input handler with formatting
  const handlePhoneInput = (value: string) => {
    const formatted = formatPhoneDisplay(value);
    onInputChange('phone', formatted);
  };

  return (
        <IonCard className={`${styles.registerCard} ion-text-center`}>
      <IonCardHeader className={styles.cardHeader}>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton
              fill="clear"
              className={styles.backButton}
              onClick={onGoBack}
              disabled={isDisabled}
            >
              <IonIcon icon={arrowBack} />
            </IonButton>
          </IonButtons>
          <IonTitle className={styles.cardTitle}>{t('components.AuthComponents.RegisterCard.register')}</IonTitle>
        </IonToolbar>
      </IonCardHeader>
      <IonGrid>
        <IonRow>
          <IonCol>
            <form onSubmit={(e) => { e.preventDefault(); if (onContinue) onContinue(); }}>
                <IonInput
                  className={`${styles.customInput} ion-margin-top`}
                  labelPlacement="stacked"
                  label={t('components.AuthComponents.RegisterCard.firstName')}
                  type="text"
                  placeholder={t('components.AuthComponents.RegisterCard.firstName')}
                  value={formData.firstName}
                  onIonInput={(e) => onInputChange('firstName', e.detail.value!)}
                  disabled={isDisabled}
                  required
                />
                <IonInput
                  className={`${styles.customInput} ion-margin-top`}
                  labelPlacement="stacked"
                  label={t('components.AuthComponents.RegisterCard.lastName')}
                  type="text"
                  placeholder={t('components.AuthComponents.RegisterCard.lastName')}
                  value={formData.lastName}
                  onIonInput={(e) => onInputChange('lastName', e.detail.value!)}
                  disabled={isDisabled}
                  required
                />
                <IonInput
                  className={`${styles.customInput} ion-margin-top`}
                  labelPlacement="stacked"
                  label={t('components.AuthComponents.RegisterCard.email')}
                  type="email"
                  placeholder="email@domain.com"
                  value={formData.email}
                  onIonInput={(e) => onInputChange('email', e.detail.value!)}
                  disabled={isDisabled}
                  required
                >
                  <IonIcon icon={mail} slot="start" />
                </IonInput>
                <IonInput
                  className={`${styles.customInput} ion-margin-top`}
                  labelPlacement="stacked"
                  label={t('components.AuthComponents.RegisterCard.phoneNumber')}
                  type="tel"
                  placeholder="+1 (555) 123-4567"
                  value={formData.phone}
                  onIonInput={(e) => handlePhoneInput(e.detail.value!)}
                  disabled={isDisabled}
                  required
                  color={formData.phone && !isValidPhoneFormat(formData.phone) ? "danger" : undefined}
                />
                {formData.phone && !isValidPhoneFormat(formData.phone) && (
                  <div className={`${styles.errorText} ion-margin-start`}>{t('components.AuthComponents.RegisterCard.phoneMustIncludeCountryCodeE')}
                  </div>
                )}
                
              <IonRow className={styles.centerCol}>
                <IonCol size="auto">
                  <IonButton
                    expand="block"
                    className={`ion-margin-top ion-margin-bottom ${styles.customButton} ${styles.mainButton}`}
                    onClick={onContinue}
                    disabled={isDisabled || !formData.firstName || !formData.lastName || !formData.email || !formData.phone || !isValidPhoneFormat(formData.phone)}
                  >{t('components.AuthComponents.RegisterCard.continue')}
                  </IonButton>
                </IonCol>
              </IonRow>
            </form>
          </IonCol>
        </IonRow>
      </IonGrid>
    </IonCard>
  );
};
